/**
 * KombiLine Mantelstein keeps its per-Zug lines apart by suffixing `configurationSourceKey` with
 * `:zug{n}` (see `kombiline-mantelstein-mit-innenrohr-system-design.ts`); lines that belong to the
 * whole chimney stay plain. Pimcore only stores the base `category-*` translations, so consumers
 * split the suffix off and re-attach it at render time.
 */

import { EW06_SYSTEM_CODE, tConnectorIsBase } from './drawingRules'

type Categorised = { category: string; sku?: string }

const ZUG_SUFFIX = /^(.*):zug(\d+)$/

/**
 * The slots of one Zug column, top to bottom. A Zug is an inner pipe from another configurator, so
 * the list spans both the KMMI-owned parts and the keys the routed systems emit: Angebot 75725 runs
 * an EW06 pipe in Zug 2 and contributes its T-Stück, Revisions-T-Stück and Kondensatschale here.
 * Everything a Zug produces beyond this list has no place in a stack and stays with its column.
 */
const ZUG_STACK_ORDER = [
  'rainHood',
  'EndPartService',
  'LengthElementService',
  'insulationPipes',
  'TConnectorService',
  'CleaningPortService',
  'removableConnection',
  'KMMI:Grundpaket',
  'FootingService',
  'condensateTray',
]

/** The same slots for a Zug standing on the Bogen mit Stützfuß: the cleaning port sits above it. */
const BASE_BEND_STACK_ORDER = [
  'rainHood',
  'EndPartService',
  'LengthElementService',
  'insulationPipes',
  'CleaningPortService',
  'TConnectorService',
  'removableConnection',
  'KMMI:Grundpaket',
  'FootingService',
  'condensateTray',
]

/** Shared lines that sit on the frame rather than inside a column. */
const TOP_PLATE = 'KMMI:Deckplatte'
const BOTTOM_PLATE = 'KMMI:Tragplatte'
/** The air shaft is a shaft, not a third Zug, so its parts arrive in the shared group. */
const AIR_SHAFT = 'KMMI:PutztuereLuftschacht'

/** Two Zug columns plus the air shaft leave too little width for the normal thumbnail. */
const NARROW_IMAGE_WIDTH = '40px'
const IMAGE_WIDTH = '46px'

export function parseCategoryKey(category: string): { base: string; zug: number | null } {
  const m = ZUG_SUFFIX.exec(category)
  return m ? { base: m[1], zug: Number(m[2]) } : { base: category, zug: null }
}

export function groupByZug<T extends Categorised>(items: T[]) {
  const shared: T[] = []
  const byZug = new Map<number, T[]>()

  for (const item of items) {
    const { zug } = parseCategoryKey(item.category)
    if (zug === null) {
      shared.push(item)
      continue
    }
    const bucket = byZug.get(zug)
    if (bucket) bucket.push(item)
    else byZug.set(zug, [item])
  }

  const zuege = [...byZug.entries()]
    .sort(([a], [b]) => a - b)
    .map(([zug, zugItems]) => ({ zug, items: zugItems }))

  return { shared, zuege }
}

/**
 * Splits a Zug's lines into the ones its column draws, ordered by `ZUG_STACK_ORDER`, and the rest.
 * A Zug does not always fill every slot: Angebot 75724 has no Regenhaube, 75717 does.
 *
 * Only an EW06 Zug brings its own T-Stück and cleaning port; Schamotte and Keramik have them in the
 * Grundpaket, PolyLine PP files its Kamineinführung under the footing. So the EW06 rule of the
 * standalone drawing decides their order: the cleaning port goes above the T-Stück only when that is
 * the Bogen mit Stützfuß.
 */
export function splitStack<T extends Categorised>(items: T[]) {
  const tConnectorSkus = items
    .filter((item) => parseCategoryKey(item.category).base === 'TConnectorService')
    .map((item) => item.sku ?? '')
  const isBaseBend = tConnectorIsBase(EW06_SYSTEM_CODE, tConnectorSkus)
  const order = isBaseBend ? BASE_BEND_STACK_ORDER : ZUG_STACK_ORDER
  const stack = order.flatMap((slot) =>
    items.filter((item) => parseCategoryKey(item.category).base === slot),
  )
  const loose = items.filter(
    (item) => !ZUG_STACK_ORDER.includes(parseCategoryKey(item.category).base),
  )
  return { stack, loose }
}

/**
 * Everything the drawing needs, derived from the calculated lines. Kept out of the component so the
 * placement can be tested; the component only arranges what comes back.
 */
export function buildKmmiLayout<T extends Categorised>(items: T[]) {
  const { shared, zuege } = groupByZug(items)
  const isBase = (item: T, base: string) => parseCategoryKey(item.category).base === base

  const plated = new Set<T>()
  const takeOne = (base: string) => {
    const found = shared.find((item) => isBase(item, base))
    if (found) plated.add(found)
    return found
  }

  // Order matters: the plates claim one line each, the rest falls through to the loose row rather
  // than disappearing.
  const topPlate = takeOne(TOP_PLATE)
  const bottomPlate = takeOne(BOTTOM_PLATE)
  const airShaft = shared.filter((item) => isBase(item, AIR_SHAFT))
  airShaft.forEach((item) => plated.add(item))

  const columns = zuege.map((zug) => ({ zug: zug.zug, ...splitStack(zug.items) }))
  const columnCount = columns.length + (airShaft.length > 0 ? 1 : 0)

  return {
    topPlate,
    bottomPlate,
    airShaft,
    columns,
    sharedLoose: shared.filter((item) => !plated.has(item)),
    imageWidth: columnCount > 2 ? NARROW_IMAGE_WIDTH : IMAGE_WIDTH,
  }
}
